import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { X, Loader2, Plus, Sparkles, Repeat } from "lucide-react";
import { commitExpansion } from "@/services/breakdownService";
import ProposalCard from "./ProposalCard";

/**
 * Right-side slide-in to review/edit/accept proposed micro-inserts for a beat.
 * Proposals are local until "Accept" persists them as child anchors.
 */
export default function ExpansionReviewPanel({ open, data, projectId, onClose, onCommitted }) {
  const [proposals, setProposals] = useState([]);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    setProposals(data?.micro_inserts ? [...data.micro_inserts] : []);
  }, [data]);

  const patch = (idx, np) => setProposals((p) => p.map((x, i) => (i === idx ? np : x)));
  const remove = (idx) => setProposals((p) => p.filter((_, i) => i !== idx));
  const move = (from, to) => setProposals((p) => {
    if (to < 0 || to >= p.length) return p;
    const next = [...p];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    return next;
  });
  const addBlank = () => setProposals((p) => [...p, {
    draft_id: `tmp-${Date.now()}`, whats_in_frame: "", shot_type: "insert",
    camera_angle: "eye-level", camera_movement: "static", lens: "macro",
    lighting: "natural", render_kind: "photoreal", prompt: "",
  }]);

  const handleAccept = async () => {
    setCommitting(true);
    try {
      const res = await commitExpansion({
        project_id: projectId,
        parent_anchor_id: data.parent_anchor_id,
        accepted: proposals,
        sting_group: data.recurring ? `STING-${data.parent_anchor_id}` : null,
      });
      toast.success(`Added ${res.child_count} micro-inserts`);
      onCommitted?.();
      onClose();
    } catch (err) {
      toast.error("Could not save inserts", { description: err.response?.data?.detail || err.message });
    } finally {
      setCommitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && data && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed right-0 top-0 bottom-0 w-[440px] max-w-[92vw] bg-[var(--bg-surface)] border-l border-[var(--border-default)] z-50 flex flex-col shadow-2xl"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.22 }}
          >
            {/* header: parent beat */}
            <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-[var(--color-gold-400)]" />
                <h2 className="text-sm font-semibold text-[var(--text-primary)] flex-1 truncate">
                  Expand · {data.beat_label}
                </h2>
                <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={16} /></button>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-[11px] text-[var(--text-muted)]">{proposals.length} micro-inserts · target ~{data.requested_count}</p>
                {data.recurring && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded border border-[rgba(212,168,83,0.3)] text-[var(--color-gold-400)] flex items-center gap-1">
                    <Repeat size={9} /> recurring sting ×{data.recurrence_count || 3}
                  </span>
                )}
              </div>
            </div>

            {/* proposals */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
              {proposals.map((p, i) => (
                <ProposalCard
                  key={p.draft_id || i}
                  proposal={p}
                  index={i}
                  total={proposals.length}
                  onChange={(np) => patch(i, np)}
                  onDelete={remove}
                  onMove={move}
                />
              ))}
              <button
                onClick={addBlank}
                className="w-full py-2 rounded-lg border border-dashed border-[var(--border-default)] text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add insert
              </button>
            </div>

            {/* footer */}
            <div className="px-4 py-3 border-t border-[var(--border-subtle)] flex items-center gap-2">
              <Button variant="ghost" onClick={onClose} disabled={committing}>Cancel</Button>
              <div className="flex-1" />
              <Button className="btn-primary" onClick={handleAccept} disabled={committing || proposals.length === 0}>
                {committing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
                Accept {proposals.length}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
